import React, { useState, useMemo, useCallback, memo } from "react"
import {
	Group,
	Menu,
	Text,
	UnstyledButton,
	TextInput,
	Box
} from "@mantine/core"
import { IconCaretDownFilled, IconSearch } from "@tabler/icons-react"
import classes from "./CountryCodePicker.module.css"
import {
	CountryCode,
	getCountries,
	getCountryCallingCode
} from "libphonenumber-js"
import { useAEMDictionary } from "@AEM/hooks/useAEMDictionary"
import { filterAndScoreItems, type SearchableItem } from "@utils/searchUtils"
import {
	VITE_FLAG_CDN_URL,
	VITE_RESTRICTED_COUNTRIES
} from "@utils/constants.util"
import { useLocale } from "@hooks/useLocale"

interface CountryOption extends SearchableItem {
	value: string
	label: string
	searchLabel: string
	flag: string
	countryCode: string
}

interface CountryCodePickerProps {
	value?: string
	onChange: (value: string) => void
	disabled?: boolean
	showLabel?: boolean
}

const CountryOptionItem = memo(
	({
		option,
		index,
		onSelect
	}: {
		option: CountryOption
		index: number
		onSelect: (value: string, countryCode: CountryCode) => void
	}) => (
		<React.Fragment key={option.id}>
			{index > 0 && <Menu.Divider />}
			<Menu.Item
				leftSection={
					<img
						className={classes.countryImage}
						src={option.flag}
						alt={`${option.searchLabel} flag`}
						width={20}
						height={15}
						loading="lazy"
					/>
				}
				onClick={() =>
					onSelect(option.value, option.countryCode as CountryCode)
				}
			>
				{option.searchLabel} (+{option.value})
			</Menu.Item>
		</React.Fragment>
	)
)

CountryOptionItem.displayName = "CountryOptionItem"

const SearchInput = memo(
	({
		searchQuery,
		setSearchQuery,
		placeholder
	}: {
		searchQuery: string
		setSearchQuery: (query: string) => void
		placeholder?: string
	}) => (
		<Group className={classes.searchContainer} gap={8} p="0 12px">
			<IconSearch size={16} />
			<TextInput
				flex={1}
				placeholder={placeholder || "Search countries..."}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.currentTarget.value)}
				size="sm"
				variant="unstyled"
				className={classes.searchInput}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			/>
		</Group>
	)
)

SearchInput.displayName = "SearchInput"

export function CountryCodePicker({
	value,
	onChange,
	disabled = false,
	showLabel = true
}: CountryCodePickerProps) {
	const [opened, setOpened] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [countryCode, setCountryCode] = useState<CountryCode>("US")
	const { dictionary } = useAEMDictionary()
	const locale = useLocale()

	const telephoneCountryOptions = useMemo(() => {
		const countries = getCountries()
		const displayLocale = locale.replace("_", "-")
		const displayNames = new Intl.DisplayNames([displayLocale], {
			type: "region"
		})

		// The company does not operate in these countries, so we don't allow them
		const restrictedCountries = VITE_RESTRICTED_COUNTRIES?.replaceAll(" ", "")
			.split(",")
			.map((country: string) => country.toLocaleUpperCase())
		return countries
			.filter(
				(countryCode) =>
					!restrictedCountries?.includes(countryCode.toLocaleUpperCase())
			)
			.map((countryCode) => {
				const callingCode = getCountryCallingCode(countryCode)
				let flagCode = countryCode.toLowerCase()
				if (countryCode === "AC" || countryCode === "TA") {
					flagCode = "sh"
				}

				return {
					value: callingCode,
					label: `+${callingCode}`,
					searchLabel:
						displayNames.of(countryCode.toUpperCase()) ||
						countryCode.toUpperCase(),
					flag: `${VITE_FLAG_CDN_URL}${flagCode}.svg`,
					countryCode: countryCode,
					id: `${countryCode}-${callingCode}`
				}
			})
			.sort((a, b) => a.searchLabel.localeCompare(b.searchLabel))
	}, [locale])

	const selectedOption = useMemo(
		() =>
			telephoneCountryOptions?.find(
				(option) => option.value === value && option.countryCode === countryCode
			),
		[telephoneCountryOptions, value, countryCode]
	)

	const filteredOptions = useMemo(() => {
		return filterAndScoreItems(telephoneCountryOptions || [], searchQuery, [
			"searchLabel",
			"value"
		])
	}, [telephoneCountryOptions, searchQuery])

	const handleOptionSelect = useCallback(
		(selectedValue: string, selectedCountry: CountryCode) => {
			onChange(selectedValue)
			setCountryCode(selectedCountry)
			setSearchQuery("")
		},
		[onChange]
	)

	const handleMenuOpen = useCallback(() => setOpened(true), [])
	const handleMenuClose = useCallback(() => {
		setOpened(false)
		setSearchQuery("")
	}, [])

	const items = useMemo(
		() => (
			<>
				<SearchInput
					searchQuery={searchQuery}
					setSearchQuery={setSearchQuery}
					placeholder={dictionary?.searchPlaceHolderLabel}
				/>

				<Box style={{ overflowY: "auto" }} mah={150}>
					{filteredOptions.map((option, index) => (
						<CountryOptionItem
							key={option.id}
							option={option}
							index={index}
							onSelect={handleOptionSelect}
						/>
					))}
				</Box>
			</>
		),
		[
			filteredOptions,
			searchQuery,
			dictionary?.searchPlaceHolderLabel,
			handleOptionSelect
		]
	)

	if (!telephoneCountryOptions?.length) {
		return null
	}

	return (
		<Menu
			onOpen={handleMenuOpen}
			onClose={handleMenuClose}
			radius="md"
			menuItemTabIndex={0}
			disabled={disabled}
		>
			<Menu.Target>
				<UnstyledButton
					className={classes.control}
					data-expanded={opened || undefined}
					h={"100%"}
					max-height={"100%"}
					w="auto"
					disabled={disabled}
				>
					<Group gap="xs">
						{selectedOption?.flag && (
							<img
								className={classes.countryImage}
								src={selectedOption.flag}
								alt={`${selectedOption.searchLabel} flag`}
								width={20}
								height={15}
								loading="lazy"
							/>
						)}
						{showLabel && (
							<Text span className={classes.label}>
								{selectedOption?.label}
							</Text>
						)}
						<IconCaretDownFilled
							size="1rem"
							className={classes.icon}
							stroke={1.5}
						/>
					</Group>
				</UnstyledButton>
			</Menu.Target>
			<Menu.Dropdown>{items}</Menu.Dropdown>
		</Menu>
	)
}
